import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "@/context/AuthContext";
import { Shell } from "@/pages/ForgotPassword";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const PW_RULES = [
  { test: (p) => p.length >= 10, label: "At least 10 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "An uppercase letter" },
  { test: (p) => /[a-z]/.test(p), label: "A lowercase letter" },
  { test: (p) => /[0-9]/.test(p), label: "A number" },
  { test: (p) => /[^A-Za-z0-9]/.test(p), label: "A symbol" },
];

export function PasswordChecklist({ value }) {
  return (
    <ul className="grid grid-cols-1 gap-1 text-xs" data-testid="pw-checklist">
      {PW_RULES.map((r) => {
        const ok = r.test(value || "");
        return (
          <li key={r.label} className={ok ? "text-emerald-600" : "text-muted-foreground"}>
            {ok ? "✓" : "○"} {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const valid = PW_RULES.every((r) => r.test(pw)) && pw === confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    const res = await resetPassword(token, pw);
    setLoading(false);
    if (res.ok) navigate("/login");
    else setError(res.error);
  };

  if (!token) {
    return <Shell title="Invalid link" subtitle="This reset link is missing or invalid.">
      <Button className="w-full" onClick={() => navigate("/login")}>Back to sign in</Button>
    </Shell>;
  }

  return (
    <Shell title="Set a new password" subtitle="Choose a strong password for your account.">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>New password</Label>
          <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required />
        </div>
        <PasswordChecklist value={pw} />
        <div className="space-y-1.5">
          <Label>Confirm password</Label>
          <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">{error}</div>}
        <Button type="submit" className="w-full" disabled={loading || !valid} data-testid="reset-submit">
          {loading ? "Saving…" : "Set password"}
        </Button>
      </form>
    </Shell>
  );
}
